import { useCallback } from "react";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import { useLogoFont } from "@/hooks/useLogoFont";
import AdminEditField from "@/components/feature/AdminEditField";
import RichText from "@/components/base/RichText";
import IconPicker from "@/components/feature/IconPicker";
import AppendableBlocks from "@/components/feature/AppendableBlocks";
import { defaultWhyWeExist, type WhyWeExistData, type WhyWeExistValue } from "@/data/siteContent";

interface WhyWeExistSectionProps { dataKey?: string; }

export default function WhyWeExistSection({ dataKey }: WhyWeExistSectionProps = {}) {
  const { isEditing: isAdmin } = useAdmin();
  const { isDark } = useDarkMode();
  const { fontFamily: headingFont, style: headingStyle } = useLogoFont();
  const [data, setData] = useAdminStorage<WhyWeExistData>(dataKey ?? "ma_why_we_exist", defaultWhyWeExist);

  const set = useCallback(<K extends keyof WhyWeExistData>(key: K, val: WhyWeExistData[K]) => {
    setData({ ...data, [key]: val });
  }, [data, setData]);

  const updateValue = useCallback((i: number, field: "title" | "body" | "icon", val: string) => {
    const values = data.values.map((v, idx) => idx === i ? { ...v, [field]: val } : v);
    setData({ ...data, values });
  }, [data, setData]);

  const addValue = useCallback(() => {
    const newValue: WhyWeExistValue = {
      icon: "ri-heart-3-line",
      title: "New Value",
      body: "Describe what makes your mission meaningful.",
    };
    setData({ ...data, values: [...data.values, newValue] });
  }, [data, setData]);

  const removeValue = useCallback((i: number) => {
    setData({ ...data, values: data.values.filter((_, idx) => idx !== i) });
  }, [data, setData]);

  const handleScrollTo = (href: string) => {
    if (href.startsWith("#")) {
      const id = href.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: "smooth" });
        return;
      }
    }
    window.open(href, "_self");
  };

  const bg = isDark ? "bg-gray-900" : "bg-white";
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const quoteBorder = isDark ? "border-pink-500/40" : "border-pink-300";
  const quoteBg = isDark ? "bg-gray-800/60" : "bg-pink-50/60";
  const cardBg = isDark ? "bg-gray-800/50 border-gray-700" : "bg-gray-50 border-gray-100";
  const iconBg = isDark ? "bg-pink-900/30" : "bg-pink-50";

  return (
    <section id="why-we-exist" className={`py-24 px-6 transition-colors duration-300 ${bg}`}>
      <div className="max-w-5xl mx-auto">
        {/* Eyebrow */}
        <div className="text-center mb-6">
          {(isAdmin || data.eyebrow) && (
            isAdmin ? (
              <AdminEditField value={data.eyebrow} onSave={(v) => set("eyebrow", v)} onDelete={() => set("eyebrow", "")} inputClassName="text-green-500 text-sm font-bold uppercase tracking-widest text-center">
                <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{data.eyebrow}</span>
              </AdminEditField>
            ) : (
              <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{data.eyebrow}</span>
            )
          )}
        </div>

        {/* Main Heading */}
        <div className="text-center mb-10">
          {(isAdmin || data.heading) && (
            isAdmin ? (
              <AdminEditField value={data.heading} onSave={(v) => set("heading", v)} onDelete={() => set("heading", "")} className="mb-4" inputClassName="text-4xl font-extrabold text-center">
                <h2 className={`text-4xl md:text-5xl font-extrabold ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{data.heading}</h2>
              </AdminEditField>
            ) : (
              <h2 className={`text-4xl md:text-5xl font-extrabold ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{data.heading}</h2>
            )
          )}
        </div>

        {/* Body */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          {(isAdmin || data.body) && (
            isAdmin ? (
              <AdminEditField value={data.body} onSave={(v) => set("body", v)} onDelete={() => set("body", "")} multiline richText inputClassName="text-lg text-center leading-relaxed">
                <RichText html={data.body} as="p" className={`text-lg leading-relaxed ${subColor}`} />
              </AdminEditField>
            ) : (
              <RichText html={data.body} as="p" className={`text-lg leading-relaxed ${subColor}`} />
            )
          )}
        </div>

        {/* Quote Block */}
        {(isAdmin || data.quote) && (
          <div className={`max-w-3xl mx-auto mb-20 rounded-2xl border-l-4 ${quoteBorder} ${quoteBg} p-8 md:p-10 relative group/quote`}>
            {isAdmin && (
              <button
                onClick={() => set("quote", "")}
                className="absolute top-3 right-3 z-20 w-7 h-7 flex items-center justify-center bg-red-100 hover:bg-red-500 text-red-400 hover:text-white rounded-full cursor-pointer transition-colors shadow-sm"
                title="Remove quote block"
              >
                <i className="ri-delete-bin-line text-xs"></i>
              </button>
            )}
            <div className="mb-4">
              <i className="ri-double-quotes-l text-pink-400 text-3xl"></i>
            </div>
            {(isAdmin || data.quote) && (
              isAdmin ? (
                <AdminEditField value={data.quote} onSave={(v) => set("quote", v)} onDelete={() => set("quote", "")} multiline richText inputClassName="text-xl italic text-center leading-relaxed">
                  <RichText html={data.quote} as="div" className={`text-xl md:text-2xl italic leading-relaxed ${headingColor}`} />
                </AdminEditField>
              ) : (
                <RichText html={data.quote} as="div" className={`text-xl md:text-2xl italic leading-relaxed ${headingColor}`} />
              )
            )}
            <div className="mt-4 flex items-center gap-2">
              <div className="w-8 h-px bg-pink-400"></div>
              {(isAdmin || data.quoteAuthor) && (
                isAdmin ? (
                  <AdminEditField value={data.quoteAuthor} onSave={(v) => set("quoteAuthor", v)} onDelete={() => set("quoteAuthor", "")} inputClassName="text-sm font-semibold">
                    <span className={`text-sm font-semibold ${subColor}`}>{data.quoteAuthor}</span>
                  </AdminEditField>
                ) : (
                  <span className={`text-sm font-semibold ${subColor}`}>{data.quoteAuthor}</span>
                )
              )}
            </div>
          </div>
        )}

        {/* Values Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16">
          {data.values.map((val, i) => (
            <div key={i} className={`rounded-2xl border p-6 text-center relative group/value transition-all hover:-translate-y-0.5 ${cardBg}`}>
              {isAdmin && (
                <button
                  onClick={() => removeValue(i)}
                  className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-red-100 hover:bg-red-500 text-red-400 hover:text-white rounded-full cursor-pointer transition-colors z-10 opacity-0 group-hover/value:opacity-100"
                  title="Remove this value"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              )}
              <div className={`w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-xl ${iconBg}`}>
                {isAdmin ? (
                  <IconPicker value={val.icon} onChange={(ic) => updateValue(i, "icon", ic)} className="absolute inset-0 flex items-center justify-center" />
                ) : (
                  <i className={`${val.icon} text-pink-500 text-2xl`}></i>
                )}
              </div>
              {isAdmin ? (
                <>
                  <AdminEditField value={val.title} onSave={(v) => updateValue(i, "title", v)} className="mb-2" inputClassName="font-bold text-base text-center">
                    <h4 className={`font-bold text-base mb-2 ${headingColor}`}>{val.title}</h4>
                  </AdminEditField>
                  <AdminEditField value={val.body} onSave={(v) => updateValue(i, "body", v)} multiline richText inputClassName="text-sm leading-relaxed text-center">
                    <RichText html={val.body} as="p" className={`text-sm leading-relaxed ${subColor}`} />
                  </AdminEditField>
                </>
              ) : (
                <>
                  <h4 className={`font-bold text-base mb-2 ${headingColor}`}>{val.title}</h4>
                  <RichText html={val.body} as="p" className={`text-sm leading-relaxed ${subColor}`} />
                </>
              )}
            </div>
          ))}

          {/* Add value card (admin only) */}
          {isAdmin && (
            <button
              onClick={addValue}
              className={`rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-2 p-6 cursor-pointer transition-all hover:border-pink-400 hover:bg-pink-50/50 min-h-[180px] ${
                isDark ? "border-gray-600 text-gray-500 hover:text-pink-400" : "border-pink-200 text-gray-400 hover:text-pink-500"
              }`}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-pink-100">
                <i className="ri-add-line text-pink-500 text-xl"></i>
              </div>
              <span className="text-sm font-semibold">Add Value</span>
            </button>
          )}
        </div>

        {/* CTA */}
        {(isAdmin || data.ctaLabel) && (
          <div className="text-center relative group/cta">
            {isAdmin && (
              <button
                onClick={() => set("ctaLabel", "")}
                className="absolute -top-3 right-1/2 translate-x-8 z-20 w-7 h-7 flex items-center justify-center bg-red-100 hover:bg-red-500 text-red-400 hover:text-white rounded-full cursor-pointer transition-colors shadow-sm"
                title="Remove CTA"
              >
                <i className="ri-delete-bin-line text-xs"></i>
              </button>
            )}
            {(isAdmin || data.ctaLabel) && (
              isAdmin ? (
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <AdminEditField value={data.ctaLabel} onSave={(v) => set("ctaLabel", v)} onDelete={() => set("ctaLabel", "")} inputClassName="text-base font-bold text-center">
                    <button
                      onClick={() => handleScrollTo(data.ctaHref)}
                      className="inline-flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white font-bold px-8 py-3.5 rounded-full cursor-pointer transition-colors whitespace-nowrap"
                    >
                      {data.ctaLabel}
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </AdminEditField>
                  <AdminEditField value={data.ctaHref} onSave={(v) => set("ctaHref", v)} inputClassName="text-xs font-mono text-center max-w-[200px]">
                    <span className={`text-xs font-mono ${subColor}`}>→ {data.ctaHref}</span>
                  </AdminEditField>
                </div>
              ) : (
                <button
                  onClick={() => handleScrollTo(data.ctaHref)}
                  className="inline-flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white font-bold px-8 py-3.5 rounded-full cursor-pointer transition-colors whitespace-nowrap"
                >
                  {data.ctaLabel}
                  <i className="ri-arrow-right-line"></i>
                </button>
              )
            )}
          </div>
        )}

        <AppendableBlocks blocks={data.extraBlocks ?? []} onChange={(v) => set("extraBlocks", v)} className="mt-10" />
      </div>
    </section>
  );
}